import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { generateTenantLaunchReadiness, getTenantLaunchReadiness } from '../api/launchReadinessApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { InfoCard } from './InfoCard';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { Button } from './ui/Button';
import {
  formatCheckStatus,
  formatLaunchStatus,
  formatReadinessCheckTitle,
  formatReadinessMessage,
  formatReadinessRecommendation,
  safeTranslate,
} from '../utils/systemLabels';

const { semantic } = colorTokens;

const CATEGORY_LABEL_KEYS = {
  billing: 'categoryBilling',
  access: 'categoryAccess',
  modules: 'categoryModules',
  setup: 'categorySetup',
  provisioning: 'categoryProvisioning',
  schema: 'categorySchema',
  storage: 'categoryStorage',
  providers: 'categoryProviders',
  permissions: 'categoryPermissions',
  subscription_changes: 'categorySubscriptionChanges',
  support: 'categorySupport',
  sms: 'categorySms',
  lifecycle: 'categoryLifecycle',
};

function checkTone(status) {
  if (status === 'passed' || status === 'ready' || status === 'go') return 'Success';
  if (status === 'warning' || status === 'go_with_warnings') return 'Warning';
  if (status === 'failed' || status === 'blocked' || status === 'no_go') return 'Danger';
  if (status === 'pending' || status === 'info') return 'Info';
  return 'Neutral';
}

export function TenantLaunchReadinessPanel({ tenantId }) {
  const { t } = useTranslation(['launchReadiness', 'common']);
  const { session } = useAuth();
  const canGenerate = hasPermission(session, 'system.tenants.launch_readiness.generate');

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getTenantLaunchReadiness(tenantId);
      setReport(data);
    } catch {
      setError(t('launchReadiness:load_failed'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const data = await generateTenantLaunchReadiness(tenantId);
      setReport(data);
    } catch {
      setError(t('launchReadiness:generate_failed'));
    } finally {
      setGenerating(false);
    }
  };

  const capabilityCards = useMemo(() => ([
    { key: 'can_prepare', label: t('launchReadiness:canPrepare') },
    { key: 'can_provision_database', label: t('launchReadiness:canProvisionDatabase') },
    { key: 'can_run_schema', label: t('launchReadiness:canRunSchema') },
    { key: 'can_seed', label: t('launchReadiness:canSeed') },
    { key: 'can_launch', label: t('launchReadiness:canLaunch') },
  ]), [t]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  return (
    <Box>
      <SectionCard
        variant="Default"
        title={t('launchReadiness:title')}
        subtitle={t('launchReadiness:description')}
        actions={canGenerate ? (
          <Button variant="Primary" onClick={handleGenerate} loading={generating} disabled={generating}>
            {t('launchReadiness:generate')}
          </Button>
        ) : null}
      >
        {error && <Alert severity="error" sx={{ mb: report ? 2 : 0 }}>{error}</Alert>}

        {report && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <StatusBadge
                status={report.status}
                category="generic"
                apiLabel={formatLaunchStatus(report.status)}
                size="Md"
                tone={checkTone(report.status)}
              />
              {report.generated_at && (
                <Typography sx={{ fontSize: 12, color: semantic.text.muted }}>
                  {t('launchReadiness:latestReport')}: {new Date(report.generated_at).toLocaleString()}
                </Typography>
              )}
            </Stack>

            <Grid container spacing={2}>
              {capabilityCards.map((card) => {
                const allowed = Boolean(report[card.key]);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.key}>
                    <InfoCard label={card.label}>
                      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
                        <StatusBadge
                          status={allowed ? 'yes' : 'no'}
                          apiLabel={allowed ? t('common:yes') : t('common:no')}
                          size="Sm"
                          tone={allowed ? 'Success' : 'Neutral'}
                        />
                      </Box>
                    </InfoCard>
                  </Grid>
                );
              })}
            </Grid>

            {report.blockers?.length > 0 && (
              <SectionCard variant="Danger" title={t('launchReadiness:blockers')} sx={{ mb: 0 }}>
                <List dense disablePadding>
                  {report.blockers.map((item) => (
                    <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={formatReadinessMessage(item) || item}
                        primaryTypographyProps={{ fontSize: 13, color: semantic.text.secondary }}
                      />
                    </ListItem>
                  ))}
                </List>
              </SectionCard>
            )}

            {report.warnings?.length > 0 && (
              <SectionCard variant="Warning" title={t('launchReadiness:warnings')} sx={{ mb: 0 }}>
                <List dense disablePadding>
                  {report.warnings.map((item) => (
                    <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={formatReadinessMessage(item) || item}
                        primaryTypographyProps={{ fontSize: 13, color: semantic.text.secondary }}
                      />
                    </ListItem>
                  ))}
                </List>
              </SectionCard>
            )}

            {report.recommendations?.length > 0 && (
              <SectionCard variant="Default" title={t('launchReadiness:recommendations')} sx={{ mb: 0 }}>
                <List dense disablePadding>
                  {report.recommendations.map((item) => (
                    <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={formatReadinessRecommendation(item) || item}
                        primaryTypographyProps={{ fontSize: 13, color: semantic.text.secondary }}
                      />
                    </ListItem>
                  ))}
                </List>
              </SectionCard>
            )}

            {Object.entries(report.checks || {}).map(([category, items]) => {
              if (!items?.length) {
                return null;
              }

              const labelKey = CATEGORY_LABEL_KEYS[category];

              return (
                <SectionCard
                  key={category}
                  variant="Default"
                  title={labelKey
                    ? safeTranslate(t, `launchReadiness:${labelKey}`, category)
                    : category}
                  sx={{ mb: 0 }}
                >
                  <Stack spacing={1.5}>
                    {items.map((check) => (
                      <Box key={check.check_code}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                          <Typography
                            sx={{
                              fontFamily: typographyTokens.fontFamily,
                              fontSize: 13,
                              fontWeight: typographyTokens.fontWeightSemibold,
                              color: semantic.text.primary,
                            }}
                          >
                            {formatReadinessCheckTitle(check)}
                          </Typography>
                          <StatusBadge
                            status={check.status}
                            apiLabel={formatCheckStatus(check.status) || check.status}
                            size="Sm"
                            tone={checkTone(check.status)}
                          />
                        </Stack>
                        <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                          {formatReadinessMessage(check)}
                        </Typography>
                        {check.remediation && (
                          <Typography sx={{ mt: 0.5, fontSize: 12, color: semantic.text.muted }}>
                            {formatReadinessRecommendation(
                              check.remediation_key || check.metadata?.remediation_key || check.remediation,
                            )}
                          </Typography>
                        )}
                        {check.check_code === 'latest_provisioning_run_success' && check.metadata?.latest_run_id && (
                          <Typography sx={{ mt: 0.5, fontSize: 12, color: semantic.text.muted }}>
                            {t('launchReadiness:provisioningRunLink', { runId: check.metadata.latest_run_id })}
                          </Typography>
                        )}
                        {check.check_code === 'latest_schema_run_status' && check.metadata?.latest_schema_run_id && (
                          <Typography sx={{ mt: 0.5, fontSize: 12, color: semantic.text.muted }}>
                            {t('launchReadiness:schemaRunLink', { runId: check.metadata.latest_schema_run_id })}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </SectionCard>
              );
            })}
          </Stack>
        )}
      </SectionCard>
    </Box>
  );
}
